import { Check, X, Eye } from "lucide-react";
import { memo } from "react";

import { type OnModerationOrderDto } from "@/common/api";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Button,
  TruncateTooltip,
} from "@/common/components";

interface ModerationRequestsTableProps {
  requests: OnModerationOrderDto[];
  isLoading: boolean;
  onApprove: (requestId: string) => void;
  onReject: (requestId: string) => void;
  onDelete: (requestId: string) => void;
  onViewDetails: (requestId: string) => void;
  isPending: boolean;
}

export const ModerationRequestsTable = memo(function ModerationRequestsTable({
  requests,
  isLoading,
  onApprove,
  onReject,
  onDelete: _onDelete,
  onViewDetails,
  isPending,
}: ModerationRequestsTableProps) {
  if (isLoading) {
    return (
      <div className="text-center py-8">
        <p className="caption">Загрузка заявок...</p>
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="caption">Заявки на модерации отсутствуют</p>
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Номер заявки</TableHead>
          <TableHead>Станция (дорога) отправления</TableHead>
          <TableHead>Груз</TableHead>
          <TableHead className="w-[200px]">Компания</TableHead>
          <TableHead>Заявитель</TableHead>
          <TableHead>Дата создания заявки</TableHead>
          <TableHead>Действия</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {requests.map((request) => (
          <TableRow
            key={request.orderId}
            className="cursor-pointer"
            onClick={() => {
              if (request.orderId) {
                onViewDetails(request.orderId);
              }
            }}
          >
            <TableCell className="font-medium">
              <div className="flex items-center gap-2">{request.orderNumber || "-"}</div>
            </TableCell>
            <TableCell>{request.loadingStation || "-"}</TableCell>
            <TableCell>
              <TruncateTooltip text={request.cargoName || "-"} className="font-medium" />
            </TableCell>
            <TableCell className="w-[200px]">
              <div className="w-[200px]">
                <TruncateTooltip text={request.company?.name || "-"} className="font-medium" />
                <div className="text-sm text-muted-foreground">
                  ИНН: {request.company?.inn || "-"}
                </div>
                {request.applicant?.phone && (
                  <div className="text-sm text-muted-foreground">
                    Телефон: {request.applicant.phone}
                  </div>
                )}
              </div>
            </TableCell>
            <TableCell>{request.applicant?.username || "-"}</TableCell>
            <TableCell>
              {request.orderCreation
                ? new Date(request.orderCreation).toLocaleDateString("ru-RU")
                : ""}
            </TableCell>
            <TableCell>
              <div className="flex items-center gap-2">
                {request.orderId && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewDetails(request.orderId!);
                    }}
                  >
                    <Eye className="h-4 w-4" />
                    <span className="hidden md:block">Детали</span>
                  </Button>
                )}
                {request.orderId && (
                  <>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        onApprove(request.orderId!);
                      }}
                      disabled={isPending}
                      className="btn-primary"
                      size="sm"
                    >
                      <Check className="h-4 w-4 mr-1" />
                      <span className="hidden md:block">Утвердить</span>
                    </Button>
                    <Button
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        onReject(request.orderId!);
                      }}
                      disabled={isPending}
                      className="text-red-600 hover:text-white"
                      size="sm"
                    >
                      <X className="h-4 w-4 mr-1" />
                      <span className="hidden md:block">Отклонить</span>
                    </Button>
                  </>
                )}
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
});
